/** 금칙어가 포함됐을 때 사용자에게 노출하는 문구 */
export const PROFANITY_ERROR_MESSAGE = '사용할 수 없는 단어가 포함되어 있어요.';

export type ProfanityLevel = 'high' | 'medium' | 'low';

export interface ProfanityRule {
    /**
     * high   : 명백한 욕설 / 혐오·차별 표현
     * medium : 성적 비속어 등 문맥에 따라 정상 단어일 수 있는 표현
     * low    : 가벼운 모욕 표현
     */
    level: ProfanityLevel;
    /** 어떤 표현을 거르는지 (로그/QA 용) */
    label: string;
    /** 정규화된 문자열과 대조할 패턴. 한글과 영문 소문자만 사용한다 */
    regex: RegExp;
}

/**
 * 금칙어 탐지 규칙 (한국어 기준)
 *
 * 검사 전에 입력값을 정규화(NFC → 공백·특수문자·숫자 제거 → 반복 문자 축약 → 자모 결합)하므로
 * `시 발`, `시1발`, `시@발`, `ㅅㅣ발`, `시!!!발` 같은 우회 표현도 같은 패턴으로 걸린다.
 * 정상 단어에 섞여 오탐이 나는 표현은 PROFANITY_WHITELIST 로 제외한다.
 * @see normalizeProfanityText
 */
export const PROFANITY_RULES: ProfanityRule[] = [
    // ───────────── 강한 욕설 ─────────────
    { level: 'high', label: '씨발', regex: /[시씨쉬쒸씌싀슈][발벌팔빨펄뻘]/ },
    { level: 'high', label: '씨발(자모 혼용)', regex: /[ㅅㅆ][발벌팔빨]|[시씨]ㅂ/ },
    { level: 'high', label: '씹', regex: /씹(?![다어은을고지었히혀는])/ },
    { level: 'high', label: '좆', regex: /[좆좃죶쫒]|[좆좃][같까]/ },
    { level: 'high', label: '존나', regex: /존[나내네낫니]|졸라(?![매맨])/ },
    { level: 'high', label: '개새끼', regex: /개[새세쉐색쌔][끼키기꺄]/ },
    { level: 'high', label: '개-욕설', regex: /개소리|개수작|개자식|개놈|개같|개지랄|개판이|개무시/ },

    // ───────────── 일반 욕설 ─────────────
    { level: 'high', label: '병신', regex: /[병븅빙벙][신씬쉰]/ },
    { level: 'high', label: '지랄', regex: /[지쥐][랄럴]/ },
    { level: 'high', label: '새끼', regex: /[새쌔쎄색섹]끼|[새쌔]키/ },
    { level: 'high', label: '염병', regex: /염[병뼝벙]/ },
    { level: 'high', label: '미친', regex: /미친[놈년새것들]/ },
    { level: 'high', label: '또라이', regex: /또라이|돌아이|돌대가리/ },
    { level: 'high', label: '욕설(명령)', regex: /꺼져라|닥쳐|뒈져|뒤져라|엿먹|디져라|주겨/ },
    { level: 'high', label: '쌍-욕설', regex: /쌍년|쌍놈|잡놈|후레자식|호로자식/ },

    // ───────────── 성적 비속어 ─────────────
    { level: 'medium', label: '보지', regex: /보지(?![마말못않는도를요만자고기며서식원사면])/ },
    { level: 'medium', label: '자지', regex: /자지(?![마말않는도를요만어고내낼며서])/ },
    { level: 'medium', label: '성기·성행위', regex: /좆물|불알|고환|젖탱|젖가슴|딸딸이|딸감|섹스|섹디|색스|성관계|떡치/ },
    { level: 'medium', label: '음란물', regex: /야동|야설|포르노|음란물|몰카|리벤지포르노/ },
    { level: 'medium', label: '자위', regex: /자위(?![대권적단함])/ },
    { level: 'medium', label: '정액', regex: /정액(?![제권])/ },
    { level: 'medium', label: '성범죄', regex: /강간|성폭행|성추행|몸캠|조건만남|원조교제|성매매/ },
    { level: 'medium', label: '창녀', regex: /창녀|창년|걸레년|화냥년|매춘부/ },

    // ───────────── 모욕·비하 ─────────────
    { level: 'low', label: '찐따', regex: /찐따|찌질이|찌질한|쪼다|찐빠/ },
    { level: 'low', label: '등신', regex: /등신(?![대불])|머저리|얼간이|바보천치/ },
    { level: 'low', label: '멍청이', regex: /멍청이|멍청한|무뇌아|저능아/ },
    { level: 'low', label: '정신병자', regex: /정신병자|미친놈아|또라이새/ },

    // ───────────── 패드립 ─────────────
    { level: 'high', label: '패드립', regex: /니미|늬미|니애미|니애비|애미없|애비없|엠창|엄창/ },
    { level: 'high', label: '패드립(느금)', regex: /느금마|느그마|느그매|느그애미|느검마|늑음마/ },
    { level: 'high', label: '패드립(부모)', regex: /애미뒤|애비뒤|엄마없|아빠없|고아새/ },

    // ───────────── 차별·혐오 ─────────────
    { level: 'high', label: '성별 비하', regex: /한남충|김치녀|된장녀|맘충|보슬아치|김치남|퐁퐁남/ },
    { level: 'high', label: '연령 비하', regex: /급식충|틀딱|틀니딱딱|노인네들|잼민이|설명충/ },
    { level: 'high', label: '인종·국가 비하', regex: /짱깨|쪽바리|쪽발이|깜둥이|흑형|양키새|짱골라/ },
    { level: 'high', label: '지역 비하', regex: /전라디언|홍어새|홍어족/ },
    { level: 'high', label: '장애 비하', regex: /절름발이|앉은뱅이|귀머거리|벙어리|외눈박이/ },

    // ───────────── 초성·자모 변형 ─────────────
    { level: 'high', label: '초성 욕설', regex: /[ㅅㅆ][ㅂㅃ]|ㅄ|ㅂㅅ|ㅈㄹ|ㅁㅊ|ㅅㄲ|ㄷㅊ|ㅈㄴ|ㅈ같|ㄱㅅㄲ/ },
    { level: 'high', label: '영문 변형', regex: /shibal|sibal|ssibal|jonna|byungsin|gaesaekki|nigger/ },
    // 한글 자판 상태로 입력한 우회 표현 (tlqkf = 시발)
    { level: 'high', label: '자판 변형', regex: /tlqkf|qudtls|wlfkf|djqtdj/ },
];

/**
 * 금칙어 패턴에 걸리지만 정상적으로 쓰이는 표현
 * 검사 전에 정규화된 문자열에서 제거해 오탐을 막는다.
 */
export const PROFANITY_WHITELIST = [
    // 시발
    '시발점', '시발역', '시발자동차', '시발지',
    // 새끼 - 신체/동물
    '새끼손', '새끼발', '새끼고양이', '새끼강아지', '새끼오리', '새끼돼지',
    '새끼사자', '새끼호랑이', '새끼곰', '새끼새',
    // 개 - 정상 합성어 (무지개같은, 번개색 등의 오탐 방지)
    '무지개', '번개', '소개', '공개', '전개', '중개', '개발', '개성', '개인',
    '개나리', '개구리', '개그', '개미', '개봉', '개편', '개학', '개막', '개운',
    // 염병 - 감염병/화염병
    '감염병', '전염병', '화염병', '폐렴병',
    // 등신
    '등신대', '등신불',
    // 병신년(丙申年)
    '병신년생', '병신년도',
    // 기타
    '닥쳐온', '벙어리장갑', '정보지', '홍보지', '전자지도',
    '자위대', '자위권', '정액제', '정액권', '졸라매', '졸라맨',
    '다시발행', '다시발급', '다시발송', '다시발표', '다시발생',
];
